import {
  AppWindow,
  Cable,
  Globe2,
  Palette,
  RefreshCw,
  ServerCog,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import type { Messages } from "@/lib/i18n";
import packshotTv6Ultra from "@/assets/products/tv6-ultra/home-hardware-packshot.jpg";

/**
 * Single source of truth for the primary site navigation. Mirrors the
 * `build(ctx) => config` pattern used by the product pages (see AGENTS.md):
 * `buildSiteNav` turns localized messages + a URL localizer into a typed
 * structure that both the desktop mega-menu and the mobile accordion render.
 */

export type NavLeaf = {
  label: string;
  desc: string;
  href: string;
  icon?: LucideIcon;
  /** Optional pill shown next to the label (e.g. "Flagship"). */
  badge?: string;
  /** Short spec surfaced inline on mobile where the full desc is too long. */
  mobileMeta?: string;
  /**
   * Optional section heading. Items sharing a `group` are rendered under one
   * label; the label shows once on the first item of each contiguous run.
   */
  group?: string;
  /**
   * Marks the item as the flagship offering. Renders a filled brand-colored
   * icon tile and a sparkle accent next to the label to set it apart.
   */
  flagship?: boolean;
};

/** Right-hand promo panel for a "featured" menu layout. */
export type NavFeatured = {
  image: string;
  imageAlt: string;
  title: string;
  desc: string;
  href: string;
  ctaLabel: string;
  footerHref: string;
  footerLabel: string;
};

export type NavEntry =
  | { kind: "link"; label: string; href: string }
  | {
      kind: "menu";
      id: string;
      label: string;
      eyebrow: string;
      /** The section hub page the title links to (e.g. /products, /solutions). */
      overviewHref: string;
      items: NavLeaf[];
      /** Present → two-column featured layout. Otherwise: grouped items
       * (any item has a `group`) → column-per-group; else a plain icon grid. */
      featured?: NavFeatured;
    };

export type NavContext = {
  m: Messages;
  /** Localizes a path to the active locale (bound `localizeUrl`). */
  u: (path: string) => string;
};

export function buildSiteNav({ m, u }: NavContext): NavEntry[] {
  return [
    {
      kind: "menu",
      id: "products",
      label: m.navProducts,
      eyebrow: m.navMenuProductsEyebrow,
      overviewHref: u("/products"),
      items: [
        {
          label: "inext TV6 Ultra",
          desc: m.productMenuTv6UltraDesc,
          href: u("/products/inext-tv6-ultra"),
          badge: m.productMenuFlagship,
          mobileMeta: "4 GB / 32 GB",
        },
        {
          label: "inext TV6",
          desc: m.productMenuTv6Desc,
          href: u("/products/inext-tv6"),
          mobileMeta: "2 GB / 16 GB",
        },
        {
          label: "inext TV5 Ultra",
          desc: m.productMenuTv5UltraDesc,
          href: u("/products/inext-tv5-ultra"),
          mobileMeta: "2 GB / 16 GB",
        },
        {
          label: "inext TV5",
          desc: m.productMenuTv5Desc,
          href: u("/products/inext-tv5"),
          mobileMeta: "1 GB / 8 GB",
        },
        {
          label: m.navAccessories,
          desc: m.productMenuAccessoriesDesc,
          href: u("/products/accessories"),
          icon: Cable,
        },
      ],
      featured: {
        image: packshotTv6Ultra,
        imageAlt: "inext TV6 Ultra",
        title: m.productMenuFeaturedTitle,
        desc: m.productMenuFeaturedDesc,
        href: u("/products/inext-tv6-ultra"),
        ctaLabel: m.productMenuFeaturedCta,
        footerHref: u("/products"),
        footerLabel: m.compareAllModels,
      },
    },
    {
      kind: "menu",
      id: "solutions",
      label: m.navSolutions,
      eyebrow: m.navMenuSolutionsEyebrow,
      overviewHref: u("/solutions"),
      items: [
        {
          label: m.navSolutionsAlcatraz,
          desc: m.navSolutionsAlcatrazDesc,
          href: u("/solutions/alcatraz-dms"),
          icon: ServerCog,
          group: m.navMenuSolutionsGroupPlatform,
          flagship: true,
        },
        {
          label: m.navSolutionsOta,
          desc: m.navSolutionsOtaDesc,
          href: u("/solutions/ota-updates"),
          icon: RefreshCw,
          group: m.navMenuSolutionsGroupPlatform,
        },
        {
          label: m.navSolutionsCustomization,
          desc: m.navSolutionsCustomizationDesc,
          href: u("/solutions/customization"),
          icon: Palette,
          group: m.navMenuSolutionsGroupServices,
        },
        {
          label: m.navSolutionsLogistics,
          desc: m.navSolutionsLogisticsDesc,
          href: u("/solutions/logistics"),
          icon: Globe2,
          group: m.navMenuSolutionsGroupServices,
        },
        {
          label: m.navSolutionsIptvApp,
          desc: m.navSolutionsIptvAppDesc,
          href: u("/solutions/iptv-app"),
          icon: AppWindow,
          group: m.navMenuSolutionsGroupApps,
        },
        {
          label: m.remoteAppMenuLabel,
          desc: m.navSolutionsRemoteAppDesc,
          href: u("/solutions/inext-remote"),
          icon: Smartphone,
          group: m.navMenuSolutionsGroupApps,
        },
      ],
    },
    { kind: "link", label: m.navGuides, href: u("/guides") },
    { kind: "link", label: m.navAbout, href: u("/about") },
    { kind: "link", label: m.navBlog, href: u("/blog") },
    { kind: "link", label: m.navFaq, href: u("/faq") },
    { kind: "link", label: m.navContact, href: u("/contacts") },
  ];
}
